import { Capacitor } from "@capacitor/core";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useAuth } from "../lib/auth/auth-context";
import { type RetryAssemblyResult, retryAssembly } from "../lib/captures/assembly";
import { TAKE_COPY } from "../lib/captures/take-copy";
import {
  conservedMs,
  isFeedbackTake,
  MAX_PHONE_MERGE_ATTEMPTS,
  type ServerCaptureView,
  takeScreenState,
} from "../lib/captures/take-lifecycle";
import { parseTakeMeta } from "../lib/captures/take-meta";
import {
  type TakeOverrideStore,
  takeMetaWithProject,
  withTakeOverrides,
} from "../lib/captures/take-overrides";
import { createServerCaptureFetcher, TAKES_CHANGED_EVENT } from "../lib/captures/take-runner";
import {
  forgetMissingSegmentsOfCapture,
  requestTakesRun,
  sharedTakeOverrides,
} from "../lib/captures/take-runtime";
import type { LegacyGroupInfo, TakeManifest, TakeMeta } from "../lib/captures/take-types";
import { formatTrashDuration } from "../lib/captures/trash";
import { createTakeRecorder } from "../lib/native/take-recorder";
import { useCurrentProject } from "../lib/projects/project-context";
import type { Project } from "../lib/projects/schema";
import { TakeProjectPicker } from "./TakeProjectPicker";
import { Button } from "./ui/Button";
import { Icon } from "./ui/Icon";
import { useVisiblePoll } from "./use-visible-poll";

// Accueil, prises qui n'existent encore que sur le téléphone (anti-perte
// phase 1, A9, spec §4) : « En préparation » (la réparation ne s'annonce pas
// étape par étape) et « Problème signalé » (dernier recours serveur en
// échec, « Réessayer » en ghost). Une carte par prise que `takeScreenState`
// ne cache pas. Même motif que les notices d'accueil (`TrashNotice`) :
// liseré + `role="status"` (DS §6.10) ; montée SOUS les boutons
// d'enregistrement, jamais entre la personne et eux (DS §6.11).
//
// Un cas en plus, parce que l'exécuteur ne peut pas le finir seul : la prise
// sans projet (C13 : adoptée, restaurée sans mémoire, choix abandonné sur
// l'écran de capture) — le sélecteur de projet pour CETTE prise, puis
// l'exécuteur est relancé.
//
// Rien ne propose de relancer l'analyse d'une capture complétée (C25, R61) :
// le flux « Ré-analyser » ré-extrait la transcription existante (celle de
// l'audio tronqué), et le serveur refuse de re-transcrire une capture déjà
// transcrite. Aucun texte ne promet une nouvelle analyse.

/** Relecture pendant l'assemblage serveur : rien ne bouge côté téléphone. */
const SERVER_POLL_MS = 15_000;
/**
 * Regroupe les relectures déclenchées par événement : un passage de
 * l'exécuteur en émet plusieurs, chacun coûterait un `listTakes` et une
 * lecture serveur par prise.
 */
export const RELOAD_DEBOUNCE_MS = 300;

export interface PreparingNoticeDeps {
  /** Prises du téléphone, choix de la personne déjà appliqués (`withTakeOverrides`). */
  listTakes: () => Promise<{ takes: TakeManifest[]; legacyGroups: LegacyGroupInfo[] }>;
  /** Lecteur de l'exécuteur (session vérifiée) : lève si la ligne n'a pas pu être lue. */
  readServer: (captureId: string, userId: string) => Promise<ServerCaptureView | null>;
  overrides: Pick<TakeOverrideStore, "list" | "set">;
  retryAssembly: (captureId: string) => Promise<RetryAssemblyResult>;
  /** Segments absents du serveur (S-m3) : la prise de la capture les renverra. */
  forgetMissing: (captureId: string, missing: string[]) => Promise<void>;
  requestRun: () => void;
}

function productionDeps(client: SupabaseClient): PreparingNoticeDeps {
  const overrides = sharedTakeOverrides();
  const recorder = withTakeOverrides(createTakeRecorder(), overrides);
  return {
    listTakes: () => recorder.listTakes(),
    readServer: createServerCaptureFetcher(client),
    overrides,
    retryAssembly: (captureId) => retryAssembly(client, captureId),
    forgetMissing: forgetMissingSegmentsOfCapture,
    requestRun: requestTakesRun,
  };
}

interface TakeView {
  m: TakeManifest;
  meta: TakeMeta | null;
  /** La prise complète une capture existante (C25) : sa ligne ne dit pas « prête ». */
  completing: boolean;
  server: ServerCaptureView | null;
  serverChecked: boolean;
}

interface NoticeState {
  views: TakeView[];
  online: boolean;
}

const EMPTY: NoticeState = { views: [], online: true };

async function readView(
  deps: PreparingNoticeDeps,
  m: TakeManifest,
  completing: boolean,
  online: boolean,
): Promise<TakeView> {
  const meta = parseTakeMeta(m.meta);
  const view: TakeView = { m, meta, completing, server: null, serverChecked: false };
  const serverMatters = m.state === "merged" || m.state === "handed_to_server";
  if (!online || m.active === true || !m.captureId || !meta || !serverMatters) return view;
  try {
    return {
      ...view,
      server: await deps.readServer(m.captureId, meta.userId),
      serverChecked: true,
    };
  } catch {
    // Ligne non lue (réseau, session) : jamais lue « absente ».
    return view;
  }
}

async function loadNotice(deps: PreparingNoticeDeps): Promise<NoticeState> {
  const online = navigator.onLine;
  const [{ takes }, overrides] = await Promise.all([deps.listTakes(), deps.overrides.list()]);
  const completing = new Set(overrides.filter((o) => o.complete).map((o) => o.takeId));
  const views = await Promise.all(
    takes.map((m) => readView(deps, m, completing.has(m.takeId), online)),
  );
  return { views, online };
}

// L'exécuteur ne crée jamais de capture sans projet (C13) : la prise reste
// bloquée tant que la personne ne l'a pas choisi. Une ligne serveur peut déjà
// exister (capture créée par l'écran de capture avec le projet choisi) : on ne
// redemande rien tant qu'elle n'a pas été lue absente.
function needsProject(v: TakeView, userId: string): boolean {
  const { m, meta } = v;
  // Avis vocal (C30) : jamais proposé comme capture d'un projet.
  if (isFeedbackTake(m)) return false;
  const blocked =
    m.state === "merged" ||
    m.state === "uploading" ||
    (m.state === "merge_failed" && m.mergeAttempts >= MAX_PHONE_MERGE_ATTEMPTS);
  if (!blocked || meta?.projectId) return false;
  if (!meta) return true;
  if (meta.userId !== userId) return false;
  if (m.state !== "merged" || !m.captureId) return true;
  return v.serverChecked && v.server === null;
}

const PREPARING_CLASSES =
  "flex flex-col gap-2 rounded-r-md border-l-2 border-flambeau-400 bg-flambeau-50 px-3 py-3 text-small text-ink";
const PROBLEM_CLASSES =
  "flex flex-col gap-2 rounded-r-md border-l-2 border-amber-400 bg-amber-50 px-3 py-3 text-small text-ink";

function PreparingEyebrow({ id }: { id?: string }) {
  return (
    <p
      id={id}
      className="m-0 flex items-center gap-2 text-label font-semibold uppercase tracking-wide text-flambeau-700"
    >
      <span aria-hidden className="inline-block size-2.5 rounded-full bg-flambeau-500" />
      {TAKE_COPY.badgePreparing}
    </p>
  );
}

// Une durée nulle ou inconnue ne s'affiche pas : jamais « 0 seconde ».
function ConservedTitle({ ms, id }: { ms: number; id?: string }) {
  if (ms <= 0) return null;
  return (
    <p id={id} className="m-0 font-medium">
      {TAKE_COPY.preparingTitle(formatTrashDuration(ms))}
    </p>
  );
}

// Exportée pour /ds (§ 6.19) : la référence montre le vrai composant.
export function PreparingCard({
  conservedMs,
  needsNetwork,
}: {
  conservedMs: number;
  needsNetwork: boolean;
}) {
  return (
    <div role="status" className={PREPARING_CLASSES}>
      <PreparingEyebrow />
      <ConservedTitle ms={conservedMs} />
      <p className="m-0 text-muted">{TAKE_COPY.preparingBody}</p>
      {needsNetwork && <p className="m-0 text-flambeau-900">{TAKE_COPY.needsNetwork}</p>}
    </div>
  );
}

interface ProjectCardProps {
  conservedMs: number;
  projects: Project[];
  loading: boolean;
  erreur: string | null;
  onSave: (projectId: string) => Promise<void>;
}

// Prise conservée sans projet : dire ce qui est gardé, chiffré, et lever
// l'obstacle dans la même carte (DS §6.11). Une seule action : ici, le corps
// « tu peux quitter l'app » serait faux — il faut un geste.
function ProjectCard({ conservedMs, projects, loading, erreur, onSave }: ProjectCardProps) {
  // « Sauvegarder » est relié au titre de SA carte (plusieurs prises peuvent
  // attendre un projet) ; sans durée à afficher, à l'en-tête.
  const titleId = useId();
  const [projectId, setProjectId] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  async function save() {
    setBusy(true);
    setFailed(false);
    try {
      await onSave(projectId);
    } catch {
      // Choix non gardé : rien n'a bougé, on le dit (même phrase que la carte
      // « Enregistrement retrouvé »).
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }
  const hasTitle = conservedMs > 0;
  // Le bouton n'a de sens que si le sélecteur l'a lui-même (chargement fini,
  // sans échec, au moins un projet) — sinon `TakeProjectPicker` affiche déjà
  // le seul texte qui tienne (rien, ou l'échec, ou « aucun projet »).
  const canPick = !loading && erreur === null && projects.length > 0;
  return (
    <div role="status" className={PREPARING_CLASSES}>
      <PreparingEyebrow id={hasTitle ? undefined : titleId} />
      <ConservedTitle ms={conservedMs} id={hasTitle ? titleId : undefined} />
      <TakeProjectPicker
        projects={projects}
        loading={loading}
        erreur={erreur}
        value={projectId}
        onChange={setProjectId}
        disabled={busy}
      />
      {canPick && (
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            aria-describedby={titleId}
            disabled={!projectId || busy}
            onClick={() => void save()}
          >
            {TAKE_COPY.finish}
          </Button>
        </div>
      )}
      {failed && (
        <p role="alert" className="m-0 text-red-deep">
          {TAKE_COPY.actionFailed}
        </p>
      )}
    </div>
  );
}

// « Réessayer » d'une carte « Problème signalé » : assemble-capture pour un
// dernier recours en échec ; pour un envoi de capture refusé (C20), la capture
// n'est pas sur le serveur — on relance le filet (synchro, puis l'exécuteur).
function problemRetry(
  source: "assembly" | "sync",
  viaFilet: ProblemCardProps["onRetry"],
  viaAssembly: ProblemCardProps["onRetry"],
): ProblemCardProps["onRetry"] {
  return source === "sync" ? viaFilet : viaAssembly;
}

interface ProblemCardProps {
  conservedMs: number;
  /** Nouvelle tentative : assemble-capture (dernier recours), ou le filet (envoi refusé, C20). */
  onRetry: () => Promise<{ ok: true } | { ok: false; error: string }>;
}

// Dernier recours serveur en échec (C08, C21) : même bandeau que la fiche de
// la capture. « Réessayer » relié au titre par aria-describedby (plusieurs
// cartes identiques possibles : un lecteur d'écran doit savoir laquelle).
// Exportée pour /ds (§ 6.19), comme `PreparingCard`.
export function ProblemCard({ conservedMs, onRetry }: ProblemCardProps) {
  const titleId = useId();
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function retry() {
    setRetrying(true);
    setError(null);
    const r = await onRetry();
    setRetrying(false);
    if (!r.ok) setError(r.error);
  }
  return (
    <div role="status" className={PROBLEM_CLASSES}>
      <p id={titleId} className="m-0 flex items-start gap-1.5 font-medium">
        <Icon name="alert-triangle" size={14} className="mt-px flex-none text-amber-deep" />
        <span>{TAKE_COPY.problemTitle}</span>
      </p>
      <p className="m-0 text-muted">{TAKE_COPY.problemBody(formatTrashDuration(conservedMs))}</p>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-describedby={titleId}
          onClick={() => void retry()}
          disabled={retrying}
        >
          {retrying ? TAKE_COPY.retrying : TAKE_COPY.retry}
        </Button>
      </div>
      {error && (
        <p role="alert" className="m-0 text-red-deep">
          {error}
        </p>
      )}
    </div>
  );
}

interface PreparingNoticeProps {
  /** Dépendances injectables (tests) ; défaut = natif, Supabase et magasin de l'app. */
  deps?: PreparingNoticeDeps;
}

export function PreparingNotice({ deps }: PreparingNoticeProps) {
  const { client, user } = useAuth();
  const { projects, loading: projectsLoading, erreur: projectsErreur } = useCurrentProject();
  const native = Capacitor.isNativePlatform();
  const userId = user?.id ?? null;
  const [resolved] = useState(() => deps ?? productionDeps(client));
  const [state, setState] = useState<NoticeState>(EMPTY);
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    if (!native || !userId) return;
    const seq = ++seqRef.current;
    try {
      const next = await loadNotice(resolved);
      // Réponse périmée (une lecture plus récente est partie) : ignorée.
      if (seq === seqRef.current) setState(next);
    } catch {
      // Natif indisponible : rien à montrer, le prochain événement relira.
    }
  }, [native, userId, resolved]);

  useEffect(() => {
    void load();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const refresh = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        void load();
      }, RELOAD_DEBOUNCE_MS);
    };
    window.addEventListener(TAKES_CHANGED_EVENT, refresh);
    window.addEventListener("online", refresh);
    window.addEventListener("offline", refresh);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(TAKES_CHANGED_EVENT, refresh);
      window.removeEventListener("online", refresh);
      window.removeEventListener("offline", refresh);
    };
  }, [load]);

  const awaitingServer = state.views.some((v) => v.m.state === "handed_to_server");
  useVisiblePoll(() => void load(), awaitingServer ? SERVER_POLL_MS : null);

  if (!native || !userId) return null;

  async function saveProject(v: TakeView, projectId: string) {
    if (!userId) return;
    await resolved.overrides.set({
      takeId: v.m.takeId,
      meta: takeMetaWithProject(v.meta, {
        userId,
        startedAt: v.m.startedAt,
        projectId,
        durationMs: conservedMs(v.m),
      }),
    });
    resolved.requestRun();
    await load();
  }

  async function retrySync(): Promise<{ ok: true }> {
    resolved.requestRun();
    await load();
    return { ok: true };
  }

  async function retry(captureId: string): Promise<RetryAssemblyResult> {
    const r = await resolved.retryAssembly(captureId);
    if (r.ok) {
      if (r.missing) await resolved.forgetMissing(captureId, r.missing);
      resolved.requestRun();
      await load();
    }
    return r;
  }

  const cards = state.views.flatMap((v) => {
    // Prise d'un autre compte du même téléphone : l'exécuteur ne peut rien pour
    // elle sous cette session, une carte « En préparation » resterait sans fin.
    if (v.meta && v.meta.userId !== userId) return [];
    // La ligne d'une capture à compléter est l'ANCIEN audio : jamais « prête ».
    const server = v.completing && v.m.state === "merged" ? null : v.server;
    const shown = takeScreenState(v.m, { online: state.online, server });
    if (shown.kind === "hidden") return [];
    if (shown.kind === "problem") {
      const captureId = v.m.captureId;
      if (!captureId) return [];
      const onRetry = problemRetry(shown.source, retrySync, () => retry(captureId));
      return [<ProblemCard key={v.m.takeId} conservedMs={shown.conservedMs} onRetry={onRetry} />];
    }
    if (needsProject(v, userId)) {
      return [
        <ProjectCard
          key={v.m.takeId}
          conservedMs={shown.conservedMs}
          projects={projects}
          loading={projectsLoading}
          erreur={projectsErreur}
          onSave={(projectId) => saveProject(v, projectId)}
        />,
      ];
    }
    return [
      <PreparingCard
        key={v.m.takeId}
        conservedMs={shown.conservedMs}
        needsNetwork={shown.needsNetwork}
      />,
    ];
  });
  if (cards.length === 0) return null;
  return <div className="flex flex-col gap-3">{cards}</div>;
}
